import { Link, useNavigate, useParams } from "react-router-dom";
import styles from "./customersignup.module.css";
import { useState } from "react";
import Loader from "../../../admin/components/Loader";
import { ArrowLeft, Globe, Shield, Truck } from "react-feather";
import { Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { usePlacesWidget } from "react-google-autocomplete";
import { useEffect } from "react";
import axios from "axios";
import { useWebsite } from "../../../context/WebsiteContext";
import { useSiteBranding } from "../../../hooks/useSiteBranding";
import { companyCountries } from "../../../utils/companyCountries";
import VerifyCodeBox from "./VerifyCodeBox";
import SharedPasswordFields, { publicAccountsLookup } from "../../../admin/components/SharedPasswordFields";

// After signing up, a Shop Now guest goes to sign in and then back to checkout (only /customer/ paths)
const afterSignup = () => {
  const redirect = new URLSearchParams(window.location.search).get("redirect");
  return redirect && redirect.startsWith("/customer/") ? "/sign-in?redirect=" + encodeURIComponent(redirect) : "/";
};

const CustomerSignup = () => {
  const navigate = useNavigate();
  const { str } = useParams();
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [companyDetails, setCompanyDetails] = useState(null);
  const [isRegCompany, setIsRegCompany] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  // Set when the email already has accounts and the new password needs an emailed code
  const [verifyMsg, setVerifyMsg] = useState("");
  const [verificationCode, setVerificationCode] = useState("");

  const { websiteData } = useWebsite();

  useSiteBranding("Add your account", websiteData);

  const themePrimaryColor = websiteData && websiteData?.home_config && websiteData?.home_config?.primaryColor ? websiteData?.home_config?.primaryColor : "#ef233c";
  const themeSecondaryColor = websiteData && websiteData?.home_config && websiteData?.home_config?.secondaryColor ? websiteData?.home_config?.secondaryColor : "#0a2a52";

  const { register, handleSubmit, setValue, control, watch, formState: { errors } } = useForm({
    mode: "onChange",
  });

  useEffect(() => {
    if (str) {
      axios.get(process.env.REACT_APP_API_URL + "v1/get-company-details/" + str).then((response) => {
        if (response.data.status === "SUCCESS") {
          if (response.data.data) {
            setCompanyDetails(response.data.data);
          } else {
            setTimeout(() => {
              navigate("/");
            }, 100);
          }
        }
        if (response.data.status === "ERROR") {
          setTimeout(() => {
            navigate("/");
          }, 100);
        }
      }).catch((error) => {
        setTimeout(() => {
          navigate("/");
        }, 100);
      });
    }
  }, [str, navigate]);

  const { ref } = usePlacesWidget({
    apiKey: process.env.REACT_APP_MAP_API_KEY,
    onPlaceSelected: (place) => {
      setValue("address", place.formatted_address);

      setValue("latitude", place.geometry.location.lat());
      setValue("longitude", place.geometry.location.lng());

      place.address_components.forEach((item) => {
        if (item.types.indexOf("postal_code") > -1) {
          setValue("post_code", item.long_name);
        }
      });
    },
    options: {
      types: ["address"],
      componentRestrictions: { country: companyCountries(websiteData).pickupCode.toLowerCase() }
    },
  });

  // A code was sent for this email + password; changing either needs a new one
  const watchEmail = watch("email");
  const watchPassword = watch("password");
  const watchPasswordMode = watch("password_mode");
  useEffect(() => {
    setVerifyMsg("");
    setVerificationCode("");
  }, [watchEmail, watchPassword, watchPasswordMode]);

  async function onSubmit(data, withCode) {
    if (withCode && verificationCode.length !== 6) {
      setErrorMsg("Enter the 6-digit code we sent to your email.");
      return;
    }

    data = { ...data, utoken: companyDetails?.utoken, ...(withCode ? { verification_code: verificationCode } : {}) };
    setLoading(true);
    setErrorMsg("");

    return axios.post(process.env.REACT_APP_API_URL + "v1/customer-signup", data).then((response) => {
      setLoading(false);
      if (response.data.status === "SUCCESS") {
        // Tell them the new password now applies to their other accounts before leaving
        if (response.data.shared_password_accounts > 0) {
          setVerifyMsg("");
          setSuccessMsg(response.data.message);
          setTimeout(() => navigate(afterSignup()), 4000);
        } else {
          navigate(afterSignup());
        }
      }
      if (response.data.status === "VERIFY") {
        setVerificationCode("");
        setVerifyMsg(response.data.message);
      }
      if (response.data.status === "ERROR") {
        setErrorMsg(response?.data?.message);
      }
    }).catch((error) => {
      if (error && error?.response && error?.response?.data && error?.response?.data?.message) {
        setErrorMsg(error?.response?.data?.message);
      }
      setLoading(false);
    });
  }

  return (<>
    <Loader open={loading} setOpen={setLoading.bind(this)} />

    <div className={styles.MainDiv} style={{ "--primary-color": themePrimaryColor, "--secondary-color": themeSecondaryColor }}>
      <div className={styles.LoginFormSec}>
        <div className={styles.LohinForm}>
          <Link to={"/"} className={styles.BackHomeBU}>
            <ArrowLeft size={18} />
            Back to home
          </Link>

          <div className={styles.LogoSection}>
            <img src={websiteData && websiteData?.home_config && websiteData?.home_config?.logoURL ? websiteData?.home_config?.logoURL : "/images/logo.png"} alt={websiteData && websiteData?.home_config && websiteData?.home_config?.companyName ? websiteData?.home_config?.companyName : 'EF LOGISTICS'} className={styles.LogoImage} />
            <h1 className={styles.LogoText}>{websiteData && websiteData?.home_config && websiteData?.home_config?.companyName ? websiteData?.home_config?.companyName : 'EF LOGISTICS'}</h1>
            <p className={styles.LogoSubText}>{websiteData && websiteData?.home_config && websiteData?.home_config?.heroTitle ? websiteData?.home_config?.heroTitle : 'Seamless Logistics from Pick-Up to Delivery'}</p>
          </div>

          <p className={styles.SubTitle}>Create a customer account</p>

          <form onSubmit={handleSubmit((data) => onSubmit(data, !!verifyMsg))}>
            {errorMsg && (<p className={styles.ErrorM2}>{errorMsg}</p>)}

            <div className={styles.FormRow}>
              <div className={styles.FormGroupFull}>
                <Controller
                  control={control}
                  {...register("name", { required: "This field is Required" })}
                  render={({ field }) => (<TextField
                    {...field}
                    classes={{ root: styles.inputRoot }}
                    fullWidth
                    label="Name*"
                    variant="outlined"
                  />)}
                />
                {errors.name && (<span className={styles.ErrorM}>{errors?.name?.message}</span>)}
              </div>

              <div className={styles.FormGroupHalf}>
                <Controller
                  control={control}
                  {...register("email", {
                    required: "This field is Required",
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                  render={({ field }) => (<TextField
                    {...field}
                    classes={{ root: styles.inputRoot }}
                    fullWidth
                    label="Email*"
                    variant="outlined"
                  />)}
                />
                {errors.email && (<span className={styles.ErrorM}>{errors?.email?.message}</span>)}
              </div>

              <div className={styles.FormGroupHalf}>
                <Controller
                  control={control}
                  {...register("phone", { required: "This field is Required" })}
                  render={({ field }) => (<TextField
                    {...field}
                    classes={{ root: styles.inputRoot }}
                    fullWidth
                    label="Phone*"
                    variant="outlined"
                  />)}
                />
                {errors.phone && (<span className={styles.ErrorM}>{errors?.phone?.message}</span>)}
              </div>

              <SharedPasswordFields
                variant="public"
                control={control}
                watch={watch}
                errors={errors}
                lookupAccounts={publicAccountsLookup(companyDetails?.utoken)}
                groupClassName={styles.FormGroupHalf}
                fullGroupClassName={styles.FormGroupFull}
                errorClassName={styles.ErrorM}
                inputClasses={{ root: styles.inputRoot }}
              />

              <div className={styles.FormGroupHalf}>
                <Controller
                  control={control}
                  {...register("address", { required: "This field is Required" })}
                  render={({ field }) => (<TextField
                    {...field}
                    classes={{ root: styles.inputRoot }}
                    fullWidth
                    label="Address*"
                    variant="outlined"
                    inputRef={ref} placeholder=""
                  />)}
                />
                {errors.address && (<span className={styles.ErrorM}>{errors?.address?.message}</span>)}
              </div>

              <div className={styles.FormGroupHalf}>
                <FormControl fullWidth>
                  <InputLabel id="demo-simple-select-label">Do you have a Registered Company?*</InputLabel>
                  <Controller
                    control={control}
                    {...register("registered_company", { required: "This field is required" })}
                    render={({ field }) => (<Select
                      {...field}
                      classes={{ root: styles.inputRoot }}
                      fullWidth
                      label="Do you have a Registered Company?*"
                      onChange={(e) => {
                        field.onChange(e);
                        setIsRegCompany(e.target.value);
                      }}
                    >
                      <MenuItem value="Yes">Yes</MenuItem>
                      <MenuItem value="No">No</MenuItem>
                    </Select>)}
                  />
                </FormControl>
                {errors.registered_company && (<span className={styles.ErrorM}>{errors?.registered_company?.message}</span>)}
              </div>

              {isRegCompany === "Yes" && <>
                <div className={styles.FormGroupHalf}>
                  <FormControl fullWidth>
                    <InputLabel id="demo-simple-select-label">If Yes, are you shipping the goods in the name of the company?*</InputLabel>
                    <Controller
                      control={control}
                      {...register("company_shipping", { required: "This field is required" })}
                      render={({ field }) => (<Select
                        {...field}
                        classes={{ root: styles.inputRoot }}
                        fullWidth
                        label="If Yes, are you shipping the goods in the name of the company?*"
                      >
                        <MenuItem value="Yes">Yes</MenuItem>
                        <MenuItem value="No">No</MenuItem>
                      </Select>)}
                    />
                  </FormControl>
                  {errors.company_shipping && (<span className={styles.ErrorM}>{errors?.company_shipping?.message}</span>)}
                </div>

                <div className={styles.FormGroupHalf}>
                  <Controller
                    control={control}
                    {...register("company_name", { required: "This field is Required" })}
                    render={({ field }) => (<TextField
                      {...field}
                      classes={{ root: styles.inputRoot }}
                      fullWidth
                      label="Registered Company Name*"
                      variant="outlined"
                    />)}
                  />
                  {errors.company_name && (<span className={styles.ErrorM}>{errors?.company_name?.message}</span>)}
                </div>
              </>}

              <div>
                <FormControl fullWidth>
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...register('terms', { required: 'You must accept the terms and conditions' })}
                        error={!!errors.terms}
                      />
                    }
                    label={<>
                      I agree to the{' '}
                      <Link to="/terms" target="_blank" rel="noopener noreferrer" className={`${styles.ForhotPassText}`}>
                        Terms and Conditions
                      </Link>
                    </>
                    }
                  />
                </FormControl>
                {errors.terms && (<span className={`${styles.ErrorM}`}>{errors?.terms?.message}</span>)}
              </div>
            </div>

            {/* Repeated next to the button: the top of this long form is usually scrolled out of view */}
            {verifyMsg && (<VerifyCodeBox message={verifyMsg} code={verificationCode} setCode={setVerificationCode} onResend={handleSubmit((data) => onSubmit(data, false))} />)}
            {errorMsg && (<p className={styles.ErrorM2} role="alert">{errorMsg}</p>)}
            {successMsg && (<p className={styles.SuccessM2} role="status">{successMsg}</p>)}

            <button type="submit" className={`${styles.LoginBU}`} disabled={!!successMsg}>
              <span>{verifyMsg ? "Verify & Register" : "Register"}</span>
            </button>

            <p className={`${styles.LoginFormSText}`}>Already have an account? <Link to={"/sign-in"} className={`${styles.ForhotPassText}`}>Login</Link></p>
          </form>
        </div>
      </div>


      <div className={styles.LoginBGSec}>
        <img src="/images/login-side-banner.png" alt="Logistics" className={styles.BGImage} />
        <div className={styles.Overlay}></div>

        <div className={styles.FeatureWrap}>
          <div className={styles.FeatureItem}>
            <Shield size={30} />
            <h4>Secure & Reliable</h4>
            <p>Advanced shipment protection and real-time tracking for complete peace of mind.</p>
          </div>

          <div className={styles.FeatureItem}>
            <Truck size={30} />
            <h4>Fast Transportation</h4>
            <p>Efficient road, air, and sea freight solutions with on-time delivery assurance.</p>
          </div>

          <div className={styles.FeatureItem}>
            <Globe size={30} />
            <h4>Worldwide Logistics</h4>
            <p>Connecting businesses globally with seamless supply chain and cargo management.</p>
          </div>
        </div>
      </div>
    </div>
  </>);
}

export default CustomerSignup;